// PopupSubmenuOption.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './PopupSubmenuOption.css';

// Icons
import Icon from 'svg/Icon';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import ReactDOM from 'react-dom';

const SUBMENU_CLOSE_DELAY_MS = 120;
const VIEWPORT_PADDING = 8;

/**
 * PopupSubmenuOption
 *
 * Renders a popup row that opens a nested flyout menu on hover (desktop) or tap (mobile).
 *
 * @param {string} label - Primary row label.
 * @param {string} note - Optional secondary label shown under the primary label.
 * @param {string} iconKey - Optional leading icon key.
 * @param {boolean} selected - Whether the row represents an active selection state.
 * @param {ReactNode} children - Flyout menu contents.
 *
 * @returns {ReactNode} The rendered submenu option row.
 */
const PopupSubmenuOption = React.memo(({
    label,
    note = '',
    iconKey,
    selected = false,
    children,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- REFS ----- //

    const rowRef = useRef(null);

    const flyoutRef = useRef(null);
    const closeTimeoutRef = useRef(null);

    // ----- STATE ----- //

    const [submenuOpen, setSubmenuOpen] = useState(false);
    const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });

    // ----- MEMOS ----- //

    const clearCloseTimeout = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    const openSubmenu = useCallback(() => {
        clearCloseTimeout();
        setSubmenuOpen(true);
    }, [clearCloseTimeout]);

    const scheduleCloseSubmenu = useCallback(() => {
        if (isMobile) return;
        clearCloseTimeout();
        closeTimeoutRef.current = setTimeout(() => {
            setSubmenuOpen(false);
        }, SUBMENU_CLOSE_DELAY_MS);
    }, [clearCloseTimeout, isMobile]);

    // ----- HANDLERS ----- //

    const handleRowClick = useCallback(() => {
        if (!isMobile) return;
        setSubmenuOpen((prev) => !prev);
    }, [isMobile]);

    // ----- MEMOS ----- //

    const updateFlyoutPosition = useCallback(() => {
        const rowElement = rowRef.current;
        const flyoutElement = flyoutRef.current;
        if (!rowElement || typeof window === 'undefined') return;

        const rowRect = rowElement.getBoundingClientRect();
        const flyoutWidth = flyoutElement?.getBoundingClientRect().width || 180;
        const flyoutHeight = flyoutElement?.getBoundingClientRect().height || 240;

        let left = rowRect.right + 4;
        if (left + flyoutWidth > window.innerWidth - VIEWPORT_PADDING) {
            left = Math.max(VIEWPORT_PADDING, rowRect.left - flyoutWidth - 4);
        }

        let top = rowRect.top;
        if (top + flyoutHeight > window.innerHeight - VIEWPORT_PADDING) {
            top = Math.max(VIEWPORT_PADDING, window.innerHeight - flyoutHeight - VIEWPORT_PADDING);
        }

        setFlyoutPosition({ top, left });
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!submenuOpen || isMobile) return undefined;

        updateFlyoutPosition();
        const frameId = requestAnimationFrame(updateFlyoutPosition);
        window.addEventListener('resize', updateFlyoutPosition);
        window.addEventListener('scroll', updateFlyoutPosition, true);

        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateFlyoutPosition);
            window.removeEventListener('scroll', updateFlyoutPosition, true);
        };
    }, [isMobile, submenuOpen, updateFlyoutPosition]);

    useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);

    const flyoutSurface = submenuOpen ? (
        <div
            className={`popup-submenu-flyout-surface popup-submenu-flyout-portal${isMobile ? ' mobile' : ' desktop'}`}
          style={isMobile ? {
                backgroundColor: theme.popup,
            } : {
                backgroundColor: theme.popup,
                top: flyoutPosition.top,
                left: flyoutPosition.left,
            }}
          ref={flyoutRef}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseEnter={isMobile ? undefined : openSubmenu}
          onMouseLeave={isMobile ? undefined : scheduleCloseSubmenu}
        >
            {children}
        </div>
    ) : null;

    const pageRoot = typeof document !== 'undefined'
        ? (document.getElementById('portal-root') || document.body)
        : null;

    // ----- RENDER ----- //

    return (
        <div
            className="popup-submenu-option-container"
        >

            {/* ROW */}
            <div
                className={`popup-submenu-option-row${submenuOpen ? ' open' : ''}`}
                ref={rowRef}
                onMouseEnter={isMobile ? undefined : openSubmenu}
                onMouseLeave={isMobile ? undefined : scheduleCloseSubmenu}
            >
                <PopupOption
                    iconKey={iconKey}
                  label={label}
                  note={note}
                  selected={selected}
                  rightContent={(
                        <Icon
                            iconKey={isMobile && submenuOpen ? 'expand_more' : 'chevron_right'}
                            width={16}
                            height={16}
                            color={theme.foregroundMuted}
                        />
                    )}
                  onClick={handleRowClick}
                />
            </div>

            {isMobile ? flyoutSurface : (
                flyoutSurface && pageRoot ? ReactDOM.createPortal(flyoutSurface, pageRoot) : null
            )}
        </div>
    );
});

// Dev Tools
PopupSubmenuOption.displayName = 'PopupSubmenuOption';

export default PopupSubmenuOption;
